import { MacWindow } from './MacWindow';
import React, { useState } from 'react';
import { jsPDF } from 'jspdf';
import { toPng } from 'html-to-image';
import { 
  VdpLayout, 
  VdpTemplate, 
  CSVData 
} from '../../types';
import { formatPrintSlug } from '../../utils/slugFormatter';
import { 
  Printer, 
  X, 
  FileText, 
  Layers, 
  CheckCircle2, 
  Download, 
  Barcode, 
  Loader2, 
  FileDown 
} from 'lucide-react';
import { useRealtimeCurrency } from '../../hooks/useRealtimeCurrency';
import { findSubstratePreset } from '../../utils/substratePresets';

interface JobTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  layout: VdpLayout;
  template: VdpTemplate;
  csvData: CSVData | null;
  selectedRecords: number;
  totalPages: number;
  workOrderNumber: string;
  clientName: string;
  operatorNotes: string;
}

export const JobTicketModal: React.FC<JobTicketModalProps> = ({
  isOpen,
  onClose,
  layout,
  template,
  csvData,
  selectedRecords,
  totalPages,
  workOrderNumber,
  clientName,
  operatorNotes
}) => {
  const { format, activeCurrency } = useRealtimeCurrency();
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  const rows = Number(layout.rows) || 1;
  const cols = Number(layout.cols) || 1;
  const stickersPerPage = layout.exportMode === 'single_pdf' || layout.exportMode === 'separate_pdfs' ? 1 : (rows * cols);

  const substrate = findSubstratePreset(layout.substrateType || 'standard_vinyl');
  const substrateName = layout.substrateName || substrate.name;
  const substrateGsm = layout.substrateGsm !== undefined ? layout.substrateGsm : substrate.weightGsm;
  const unitCost = layout.costPerSheet !== undefined ? layout.costPerSheet : substrate.defaultCost;
  const inkCost = layout.inkCostPerSheet !== undefined ? layout.inkCostPerSheet : 0.8;
  const totalCost = (totalPages * unitCost) + (totalPages * inkCost);

  const handleExportPdf = async () => {
    const element = document.getElementById('printable-ticket-body');
    if (!element || isExporting) return;

    setIsExporting(true);

    // Create a temporary container styled with white background and black text
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.top = '-9999px';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = '794px'; // ~210mm in pixels at 96 DPI
    tempContainer.style.background = '#ffffff';
    tempContainer.style.color = '#000000';
    tempContainer.style.padding = '32px';
    tempContainer.className = 'theme-light text-black';

    // Clone the printable ticket body
    const clone = element.cloneNode(true) as HTMLElement;
    clone.style.background = '#ffffff';
    clone.style.color = '#000000';
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.classList.remove('text-zinc-200');
    clone.classList.add('text-black');

    // Remove elements marked for export ignore (e.g. Pre-Press Marks & Dynamic Variable Fields section)
    clone.querySelectorAll('.export-ignore, [data-no-export="true"]').forEach(node => node.remove());

    // Create a style block to override dark styles to pristine high-contrast light styles
    const styleOverride = document.createElement('style');
    styleOverride.innerHTML = `
      * {
        color: #18181b !important; /* text-zinc-900 */
        border-color: #e4e4e7 !important; /* border-zinc-200 */
      }
      .bg-zinc-950, .bg-zinc-900, .bg-zinc-800 {
        background-color: #f4f4f5 !important; /* bg-zinc-100 */
        border: 1px solid #e4e4e7 !important;
      }
      .text-zinc-100, .text-zinc-200, .text-zinc-300, .text-zinc-400 {
        color: #18181b !important; /* text-zinc-900 */
      }
      .text-zinc-500, .text-zinc-600 {
        color: #71717a !important; /* text-zinc-500 */
      }
      .text-\\[\\#1473E6\\], .text-blue-700 {
        color: #1d4ed8 !important; /* blue-700 */
      }
      .bg-amber-500\\/10 {
        background-color: #fef3c7 !important; /* amber-50 */
        border-color: #fde68a !important; /* amber-200 */
      }
      .text-amber-400 {
        color: #b45309 !important; /* amber-700 */
      }
      svg {
        color: #18181b !important;
        stroke: #18181b !important;
      }
    `;
    tempContainer.appendChild(styleOverride);
    tempContainer.appendChild(clone);
    document.body.appendChild(tempContainer);

    try {
      const dataUrl = await toPng(clone, {
        cacheBust: true,
        backgroundColor: '#ffffff',
        pixelRatio: 2,
        style: {
          transform: 'scale(1)',
          transformOrigin: 'top left'
        }
      });
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      // clone width is ~794px, height is auto
      const imgHeight = (clone.clientHeight * imgWidth) / clone.clientWidth;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(dataUrl, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Job_Ticket_${workOrderNumber || 'WO-PRINT-VDP'}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      if (tempContainer && tempContainer.parentNode) {
        tempContainer.parentNode.removeChild(tempContainer);
      }
      setIsExporting(false);
    }
  };

  const handleExportJson = () => {
    const docketData = {
      workOrderNumber: workOrderNumber || `WO-${Date.now()}`,
      clientName: clientName || 'Standard Client',
      createdAt: new Date().toISOString(),
      jobSummary: {
        totalRecords: selectedRecords,
        stickersPerSheet: stickersPerPage,
        impositionMatrix: `${rows} rows x ${cols} cols`,
        totalMasterSheets: totalPages,
        sheetSize: layout.pageSize,
        stickerDimensions: `${template.width} x ${template.height} pt`,
        bleedPt: layout.bleed || 0
      },
      substrate: {
        name: substrateName,
        weightGsm: substrateGsm,
        costPerSheet: unitCost,
        currency: activeCurrency.code
      },
      productionEconomics: {
        totalEstimatedCost: totalCost,
        unitCostPerFinishedPiece: selectedRecords > 0 ? (totalCost / selectedRecords) : 0
      },
      marks: {
        showTrimMarks: Boolean(layout.showTrimMarks),
        bleedBox: Boolean(layout.bleed),
        colorBars: Boolean(layout.colorBars),
        registrationMarks: Boolean(layout.registrationMarks),
        printSlug: Boolean(layout.printSlug)
      },
      operatorNotes: operatorNotes || 'None'
    };

    const blob = new Blob([JSON.stringify(docketData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Job_Ticket_${workOrderNumber || 'Docket'}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <MacWindow isOpen={isOpen} onClose={onClose} title="Pre-Press Production Docket & Work Order" maxWidth="max-w-3xl" className="print:max-h-none print:shadow-none print:border-none">
      <div id="job-ticket-modal" className="flex flex-col w-full h-full overflow-hidden">
        
        {/* Header Bar */}
        <div className="p-4 sm:p-5 border-b border-zinc-800 bg-zinc-950 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#1473E6] text-white flex items-center justify-center shadow-sm">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-zinc-200">Pre-Press Production Docket &amp; Work Order</h3>
              <p className="text-xs text-zinc-500">Official print shop specification ticket for press operators and bindery</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleExportJson}
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-bold rounded-md flex items-center gap-1 transition-colors cursor-pointer"
              title="Download JSON Docket"
            >
              <Download className="w-3.5 h-3.5" />
              <span>JSON</span>
            </button>
            <button
              onClick={handleExportPdf}
              disabled={isExporting}
              className="px-3 py-1.5 bg-[#1473E6] hover:bg-blue-700 text-white text-xs font-bold rounded-md flex items-center gap-1 transition-colors cursor-pointer shadow-xs disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isExporting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <FileDown className="w-3.5 h-3.5" />
              )}
              <span>{isExporting ? 'Exporting...' : 'Export PDF'}</span>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-md text-zinc-400 hover:text-zinc-400 hover:bg-zinc-700 flex items-center justify-center transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Printable Ticket Body */}
        <div id="printable-ticket-body" className="p-6 sm:p-8 overflow-y-auto print:p-0 print:overflow-visible space-y-6 text-zinc-200 text-xs">
          
          {/* Ticket Header & Brand */}
          <div className="border-b-2 border-zinc-900 pb-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="text-xl font-black tracking-tight text-zinc-100 uppercase">
                PRODUCTION WORK ORDER
              </div>
              <div className="text-xs font-mono text-zinc-500 mt-0.5">
                ORDER ID: <strong className="text-zinc-100">{workOrderNumber || 'WO-PRINT-VDP'}</strong> • DATE: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
              </div>
            </div>

            <div className="text-right bg-zinc-800 px-3 py-2 rounded-md border border-zinc-700">
              <span className="text-[10px] font-bold text-zinc-500 uppercase block">Client / Account:</span>
              <span className="text-sm font-bold text-zinc-100">{clientName || 'Direct Production'}</span>
            </div>
          </div>

          {/* Key Production Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-zinc-950 p-4 rounded-md border border-zinc-800">
            <div>
              <span className="text-[10px] font-bold text-zinc-500 uppercase block">Total Run Volume</span>
              <span className="text-base font-extrabold text-[#1473E6] font-mono">{selectedRecords.toLocaleString()} pcs</span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-zinc-500 uppercase block">{layout.isRollMode ? 'Roll Meters / Web' : 'Master Sheets'}</span>
              <span className="text-base font-extrabold text-zinc-100 font-mono">
                {layout.isRollMode 
                  ? `${((totalPages * (template.height || 100) * 0.352778) / 1000).toFixed(1)} meters` 
                  : `${totalPages.toLocaleString()} sheets`}
              </span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-zinc-500 uppercase block">{layout.isRollMode ? 'Roll Track Lanes' : 'Imposition Grid'}</span>
              <span className="text-base font-extrabold text-[#1473E6] font-mono">
                {layout.isRollMode 
                  ? `${layout.trackCount || 1}-Track (${layout.rollWidthMm || 108}mm Web)` 
                  : `${stickersPerPage}-Up (${rows}×${cols})`}
              </span>
            </div>
            <div>
              <span className="text-[10px] font-bold text-zinc-500 uppercase block">Est Ink &amp; Print Time</span>
              <span className="text-base font-extrabold text-emerald-400 font-mono">
                {(layout.estimatedInkMl || (totalPages * 0.12)).toFixed(1)} mL • {(layout.estimatedPrintTimeMin || Math.max(1, Math.round(totalPages / 15)))} min
              </span>
            </div>
          </div>

          {/* Continuous Roll Label Mode Specs (Phase 4) */}
          {layout.isRollMode && (
            <div className="bg-amber-950/40 border border-amber-500/40 rounded-md p-4 space-y-2">
              <div className="flex items-center justify-between border-b border-amber-500/30 pb-1.5">
                <h4 className="font-bold text-amber-300 text-xs uppercase tracking-wider flex items-center gap-1.5">
                  <Printer className="w-3.5 h-3.5 text-amber-400" />
                  Continuous Roll Printer Specifications &amp; Unwind Direction
                </h4>
                <span className="text-[10px] font-mono font-bold bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded border border-amber-500/30">
                  ROLL FEED ACTIVE
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-[11px] pt-1">
                <div>
                  <span className="text-zinc-400 block">Printer Hardware:</span>
                  <strong className="text-amber-200">{layout.rollPrinterPreset?.toUpperCase() || 'EPSON COLORWORKS C6000'}</strong>
                </div>
                <div>
                  <span className="text-zinc-400 block">Web Width &amp; Tracks:</span>
                  <strong className="text-amber-200">{layout.rollWidthMm || 108} mm • {layout.trackCount || 1} Lane(s)</strong>
                </div>
                <div>
                  <span className="text-zinc-400 block">Sensor Gap / Eye Mark:</span>
                  <strong className="text-amber-200">{layout.sensorMarkType?.toUpperCase() || 'DIE-CUT GAP (3mm)'}</strong>
                </div>
                <div>
                  <span className="text-zinc-400 block">Unwind Direction:</span>
                  <strong className="text-amber-200 font-mono font-bold">DIRECTION #{layout.unwindDirection || 1} (Top Edge Out)</strong>
                </div>
              </div>
            </div>
          )}

          {/* Section 1: Pre-Press & Press Specification */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Box A: Imposition & Sheet Spec */}
            <div className="border border-zinc-800 rounded-md p-4 space-y-2.5">
              <h4 className="font-bold text-zinc-200 text-xs uppercase tracking-wider flex items-center gap-1.5 text-[#1473E6] border-b border-zinc-700 pb-1.5">
                <Printer className="w-3.5 h-3.5" />
                Sheet &amp; Imposition Specification
              </h4>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <span className="text-zinc-500 block">Master Sheet Size:</span>
                  <strong className="text-zinc-100">{layout.pageSize} ({layout.pageOrientation || 'Portrait'})</strong>
                </div>
                <div>
                  <span className="text-zinc-500 block">Finished Label Size:</span>
                  <strong className="text-zinc-100">{template.width} × {template.height} pt</strong>
                </div>
                <div>
                  <span className="text-zinc-500 block">Gutter Spacing (X / Y):</span>
                  <strong className="text-zinc-100">{layout.gapX || 0} pt / {layout.gapY || 0} pt</strong>
                </div>
                <div>
                  <span className="text-zinc-500 block">Margins (X / Y):</span>
                  <strong className="text-zinc-100">{layout.marginX || 0} pt / {layout.marginY || 0} pt</strong>
                </div>
                <div>
                  <span className="text-zinc-500 block">Bleed Allowance:</span>
                  <strong className="text-zinc-100">{layout.bleed || 0} pt ({((layout.bleed || 0) * 0.352778).toFixed(1)} mm)</strong>
                </div>
                <div>
                  <span className="text-zinc-500 block">Export Format:</span>
                  <strong className="text-zinc-100">{layout.exportMode === 'separate_pdfs' ? 'ZIP Multi-file' : 'Single Master PDF'}</strong>
                </div>
                <div className="col-span-2 pt-1 border-t border-zinc-800">
                  <span className="text-zinc-500 block">Printed Margin Slug Line:</span>
                  <strong className="text-emerald-400 font-mono text-[10px] break-all block">
                    {formatPrintSlug(layout, undefined, { templateWidthPt: template.width, templateHeightPt: template.height })}
                  </strong>
                </div>
              </div>
            </div>

            {/* Box B: Material & Substrate Details */}
            <div className="border border-zinc-800 rounded-md p-4 space-y-2.5">
              <h4 className="font-bold text-zinc-200 text-xs uppercase tracking-wider flex items-center gap-1.5 text-[#1473E6] border-b border-zinc-700 pb-1.5">
                <Layers className="w-3.5 h-3.5" />
                Substrate &amp; Material Profile
              </h4>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <span className="text-zinc-500 block">Material Preset:</span>
                  <strong className="text-zinc-100">{substrateName}</strong>
                </div>
                <div>
                  <span className="text-zinc-500 block">Grammage / Density:</span>
                  <strong className="text-zinc-100 font-mono">{substrateGsm} GSM</strong>
                </div>
                <div>
                  <span className="text-zinc-500 block">Material Sheet Cost:</span>
                  <strong className="text-zinc-100 font-mono">{format(unitCost)} / sheet</strong>
                </div>
                <div>
                  <span className="text-zinc-500 block">Total Substrate Cost:</span>
                  <strong className="text-zinc-100 font-mono">{format(totalPages * unitCost)}</strong>
                </div>
                <div className="col-span-2">
                  <span className="text-zinc-500 block">Material Description:</span>
                  <span className="text-zinc-300 italic">{substrate.description}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Production Marks & Variable Elements */}
          <div className="border border-zinc-800 rounded-md p-4 space-y-2.5 export-ignore" data-no-export="true">
            <h4 className="font-bold text-zinc-200 text-xs uppercase tracking-wider flex items-center gap-1.5 text-zinc-300 border-b border-zinc-700 pb-1.5">
              <Barcode className="w-3.5 h-3.5 text-purple-400" />
              Pre-Press Marks &amp; Dynamic Variable Fields
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className={`w-3.5 h-3.5 ${layout.showTrimMarks ? 'text-[#1473E6]' : 'text-zinc-300'}`} />
                <span>Crop/Trim Marks</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className={`w-3.5 h-3.5 ${layout.bleed ? 'text-[#1473E6]' : 'text-zinc-300'}`} />
                <span>Bleed Boundary</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className={`w-3.5 h-3.5 ${layout.registrationMarks ? 'text-[#1473E6]' : 'text-zinc-300'}`} />
                <span>Registration Crosshairs</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className={`w-3.5 h-3.5 ${layout.colorBars ? 'text-[#1473E6]' : 'text-zinc-300'}`} />
                <span>CMYK Color Density Bars</span>
              </div>
            </div>

            <div className="mt-2 pt-2 border-t border-zinc-700 text-[11px]">
              <span className="text-zinc-500 block mb-1">Variable Fields Configured in Template ({template.elements.length} elements):</span>
              <div className="flex flex-wrap gap-1.5">
                {template.elements.map((el, i) => (
                  <span key={el.id || i} className="px-2 py-0.5 bg-zinc-800 border border-zinc-800 rounded font-mono text-[10px] text-zinc-300">
                    {el.type.toUpperCase()}: {el.columnName ? `{${el.columnName}}` : el.name || 'Static'}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Section 3: Operator Notes & Instructions */}
          {operatorNotes && (
            <div className="p-3.5 bg-amber-500/10 border border-amber-200 rounded-md">
              <span className="text-[10px] font-bold text-amber-500 uppercase block mb-0.5">Press Operator Special Instructions:</span>
              <p className="text-xs text-amber-400 font-medium">{operatorNotes}</p>
            </div>
          )}

          {/* Section 4: Sign-off & Quality Inspection Box */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t-2 border-zinc-900 text-[11px]">
            <div className="border border-zinc-700 p-3 rounded-md flex flex-col justify-between h-20">
              <div className="flex items-center justify-between text-zinc-500">
                <span>Press Operator Sign-off:</span>
                <span>Date: ____________</span>
              </div>
              <div className="border-b border-zinc-600 border-dashed w-3/4"></div>
            </div>

            <div className="border border-zinc-700 p-3 rounded-md flex flex-col justify-between h-20">
              <div className="flex items-center justify-between text-zinc-500">
                <span>Quality Control / Bindery Inspector:</span>
                <span>Passed: [  ] YES</span>
              </div>
              <div className="border-b border-zinc-600 border-dashed w-3/4"></div>
            </div>
          </div>

          <div className="text-center text-[10px] text-zinc-400 font-mono pt-2">
            Generated via VDP Print Automation Engine • 100% Vector PDF Output Conforming to ISO Pre-Press Standards
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 bg-zinc-950 flex items-center justify-end gap-3 print:hidden">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 hover:text-zinc-100 text-xs font-bold rounded-md transition-colors cursor-pointer border border-zinc-700"
          >
            Close Ticket
          </button>
          <button
            onClick={handleExportPdf}
            disabled={isExporting}
            className="px-5 py-2 bg-[#1473E6] hover:bg-blue-700 text-white text-xs font-bold rounded-md transition-colors cursor-pointer shadow-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isExporting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            {isExporting ? 'Exporting PDF...' : 'Export PDF Ticket'}
          </button>
        </div>

      </div>
    </MacWindow>
  );
};

